import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModalInstance, useModal } from "react-modal-state";
import { Modal } from "react-bootstrap";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Icon from "../common/icons";
import SignInCodeFlow from "../common/SignInCodeFlow";
import { closedSignInText } from "../common/leavingSchool";
import anna from "../../assets/Anna_1.png";
import api from "../../utils/api";
import { hasPostLoginRedirect, takePostLoginRedirect } from "../../utils/authRedirect";
import { isStandalone } from "../../pwa/platform";
import { defaultSharedComputer, offerSharedComputer, setSharedComputer } from "../../utils/sharedComputer";
import { markSignedInHere, startFreshChatId } from "../../utils/studentTraces";
import { UserContext } from "../../context/UserContext";
import { ChatbotContext } from "../../context/ChatbotContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../styles/LoginModal.css"; // Ensure you have a CSS file for any custom styles

// How long the dialog says "You're in" before it closes
const SIGNED_IN_HOLD_MS = 700;
const EASE = [0.16, 1, 0.3, 1];

const firstName = (name) => String(name || "").trim().split(/\s+/)[0];

const LoginModal = () => {
  // data.email: the address typed in Forgot Password, carried back here
  const { isOpen, close, data } = useModalInstance();
  const { setIsAuthenticated, fetchUserData } = useContext(UserContext);
  const { setShowbot } = useContext(ChatbotContext) || {};
  const { open: openRegisterModal } = useModal("register");
  const { open: openForgotPasswordModal } = useModal("forgotPassword");
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // The student's login status once the sign-in went through; the page switches over
  // after the dialog has closed (handleExited)
  const [signedInAs, setSignedInAs] = useState(null);
  const signedInRef = useRef(null);
  const emailRef = useRef(null);
  // "password", or "code" for "Sign in with a code" (common/SignInCodeFlow.js)
  const [mode, setMode] = useState("password");
  const [passwordFailed, setPasswordFailed] = useState(false);
  // C4: the right password on an account the guidance office closed ({ closedOn, school })
  const [closedAccount, setClosedAccount] = useState(null);
  // The email carried between the password form and the code flow
  const [email, setEmail] = useState("");
  // "Shared computer: sign me out when I step away" (utils/sharedComputer.js)
  const [shared, setShared] = useState(defaultSharedComputer);

  // Each time the dialog opens, start from the password form
  useEffect(() => {
    if (!isOpen) return;
    // Sign-up's "Email me a sign-in code" opens straight on the code flow (RegisterModal, F18)
    setMode(data?.mode === "code" ? "code" : "password");
    setPasswordFailed(false);
    setClosedAccount(null);
    setEmail(data?.email || "");
    setShared(defaultSharedComputer());
  }, [isOpen]);

  // Same for a password or an emailed code: check it's a student, keep the session,
  // show "You're in" while the header's user loads, then close the dialog
  const completeSignIn = async ({ access, refresh }) => {
    const statusResponse = await api.get("/api/check_login_status/", {
      headers: { Authorization: `Bearer ${access}` },
    });
    const userData = statusResponse.data.user_data;

    if (userData.is_staff || userData.is_superuser) {
      toast.error("Only student accounts are allowed to login.");
      return;
    }

    localStorage.setItem("authToken", access);
    localStorage.setItem("refreshToken", refresh);
    localStorage.setItem("isAuthenticated", "true");
    // The chat's Node token belongs to whoever used this browser before
    localStorage.removeItem("token");
    // ...and so does the chat id: each sign-in starts its own chat (F5)
    startFreshChatId();
    setSharedComputer(offerSharedComputer() && shared);
    markSignedInHere();

    signedInRef.current = userData;
    setSignedInAs(userData);
    await Promise.all([
      Promise.resolve(fetchUserData?.()).catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, SIGNED_IN_HOLD_MS)),
    ]);
    close();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isLoggingIn || signedInAs) return;
    setIsLoggingIn(true);

    const loginData = {
      // A space or a capital letter from the phone keyboard shouldn't fail the sign-in
      email: e.target.loginEmail.value.trim().toLowerCase(),
      password: e.target.loginPassword.value,
      // The installed phone app stays signed in for 30 days instead of one (custom_auth/app_sessions.py)
      ...(isStandalone() && { installed_app: true }),
    };

    try {
      const response = await api.post("/auth/token/", loginData, {
        headers: { "Content-Type": "application/json" },
        skipErrorToast: true,
      });
      if (response.status === 200) {
        await completeSignIn(response.data);
      }
    } catch (err) {
      console.error("Login error:", err);
      const failure = err.response?.data || {};
      if (err.response?.status === 401 && failure.code === "account_closed") {
        // C4: the right password on a closed account: say so, and no code offer (it can't sign in either)
        setPasswordFailed(false);
        setClosedAccount({ closedOn: failure.closed_on, school: failure.school });
      } else if ([400, 401].includes(err.response?.status)) {
        // Wrong email or password: offer a code by email instead
        setClosedAccount(null);
        setPasswordFailed(true);
      } else {
        toast.error(
          err.response?.status === 429
            ? "Too many sign-in attempts. Wait a minute, then try again."
            : "Login failed. Check your connection and try again.",
        );
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const openCodeFlow = () => {
    setEmail(emailRef.current?.value.trim() || email);
    setMode("code");
  };

  const backToPassword = (typedEmail) => {
    if (typedEmail) setEmail(typedEmail);
    setPasswordFailed(false);
    setMode("password");
  };

  // Once the dialog has faded out, switch the app to signed in where it is (no page
  // reload): the header and hero change over and the chat opens on a clear page. A
  // page that sent the student here to sign in gets them back instead.
  const handleExited = () => {
    const userData = signedInRef.current;
    signedInRef.current = null;
    setSignedInAs(null);
    setShowPassword(false);
    if (!userData) return;

    const params = new URLSearchParams(window.location.search);
    const redirectPath = takePostLoginRedirect();
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    } else if (params.has("login") || params.has("redirect")) {
      navigate(window.location.pathname + window.location.hash, { replace: true });
    }

    setShowbot?.(true);
    setIsAuthenticated(true);
    window.dispatchEvent(new CustomEvent("loginSuccess", { detail: userData }));
  };

  const handleSignUp = () => {
    close();
    setTimeout(() => {
      openRegisterModal();
    }, 300);
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    // Hand over the email already typed so the student doesn't type it again
    const typed = emailRef.current?.value.trim() || "";
    close();
    setTimeout(() => {
      openForgotPasswordModal({ email: typed });
    }, 300);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const panel = {
    initial: (back) => ({ opacity: 0, x: reduce ? 0 : back ? -28 : 28 }),
    animate: { opacity: 1, x: 0 },
    exit: (back) => ({ opacity: 0, x: reduce ? 0 : back ? 28 : -28 }),
  };
  const panelMotion = (back) => ({
    custom: back,
    variants: panel,
    initial: "initial",
    animate: "animate",
    exit: "exit",
    transition: { duration: reduce ? 0.12 : 0.24, ease: EASE },
  });

  return (
    <Modal
      show={isOpen}
      onHide={close}
      onExited={handleExited}
      dialogClassName={`am-auth-dialog${signedInAs ? " is-signed-in" : ""}`}
      centered
      aria-labelledby="login-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="login-title">Sign In</Modal.Title>
      </Modal.Header>
      <Modal.Body className="am-auth-body">
        <AnimatePresence mode="wait" initial={false}>
          {mode === "code" ? (
            <motion.div key="code" {...panelMotion(false)}>
              <SignInCodeFlow
                portal="student"
                variant="student"
                initialEmail={email}
                onSignedIn={completeSignIn}
                onBack={backToPassword}
              />
            </motion.div>
          ) : (
            <motion.form key="password" onSubmit={handleLogin} className="am-form" {...panelMotion(true)}>
              <div className="am-intro" aria-live="polite">
                <span className="am-auth-face">
                  <img src={anna} alt="Anna" className="am-avatar" />
                  {signedInAs && (
                    <span className="am-auth-face-check" aria-hidden="true">
                      <Icon name="check" size={18} />
                    </span>
                  )}
                </span>
                {signedInAs ? (
                  <>
                    <h3 className="am-intro-title">
                      {firstName(signedInAs.name)
                        ? `Welcome back, ${firstName(signedInAs.name)}!`
                        : "Welcome back!"}
                    </h3>
                    <p className="am-intro-text">
                      {hasPostLoginRedirect()
                        ? "You're signed in. Taking you back..."
                        : "You're signed in. Opening your chat..."}
                    </p>
                  </>
                ) : (
                  // For first-timers and returning students alike (N7)
                  <h3 className="am-intro-title">Sign in to Anna</h3>
                )}
              </div>

              <label className="am-field" htmlFor="loginEmail">
                <span className="am-label">Email</span>
                <span className="am-input">
                  <Icon name="mail" size={20} />
                  <input
                    type="email"
                    id="loginEmail"
                    ref={emailRef}
                    defaultValue={email}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    readOnly={Boolean(signedInAs)}
                  />
                </span>
              </label>

              <div className="am-field">
                <span className="am-auth-label-row">
                  <label className="am-label" htmlFor="loginPassword">Password</label>
                  <a
                    href="#forgotPasswordModal"
                    className="am-link am-auth-forgot"
                    onClick={handleForgotPassword}
                  >
                    Forgot password?
                  </a>
                </span>
                <span className="am-input">
                  <Icon name="lock" size={20} />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="loginPassword"
                    placeholder="Your password"
                    autoComplete="current-password"
                    required
                    readOnly={Boolean(signedInAs)}
                    onChange={() => passwordFailed && setPasswordFailed(false)}
                  />
                  <button
                    type="button"
                    className="am-input-btn"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                  >
                    <Icon name={showPassword ? "eye-off" : "eye"} size={20} />
                  </button>
                </span>
              </div>

              {/* Lab and library computers: sign out after a quiet spell (F19). The
                  installed app is the student's own phone, so it isn't asked there. */}
              {offerSharedComputer() && !signedInAs && (
                <label className="am-choice am-auth-shared" htmlFor="loginShared">
                  <input
                    type="checkbox"
                    id="loginShared"
                    checked={shared}
                    onChange={(e) => setShared(e.target.checked)}
                  />
                  <span>
                    <b>Shared computer</b>
                    <span className="am-auth-shared-hint">Sign me out when I step away</span>
                  </span>
                </label>
              )}

              <button
                className="am-btn is-primary is-block is-large"
                type="submit"
                disabled={isLoggingIn || Boolean(signedInAs)}
              >
                {signedInAs ? (
                  <>
                    <Icon name="check" size={18} className="am-auth-done-icon" />
                    You're in
                  </>
                ) : isLoggingIn ? (
                  <>
                    <span className="spinner-border" role="status" aria-hidden="true" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <Icon name="sign-in" size={18} />
                  </>
                )}
              </button>

              {!signedInAs && (
                <AnimatePresence mode="wait" initial={false}>
                  {closedAccount ? (
                    // C4: no "Sign in with a code" here, a closed account can't sign in that way either
                    <motion.div
                      key="closed"
                      className="am-auth-tip is-closed"
                      role="alert"
                      initial={{ opacity: 0, y: reduce ? 0 : -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.22, ease: EASE }}
                    >
                      <p className="am-auth-tip-title">
                        <Icon name="info" size={18} />
                        {closedSignInText(closedAccount).title}
                      </p>
                      <p>{closedSignInText(closedAccount).text}</p>
                    </motion.div>
                  ) : passwordFailed ? (
                    <motion.div
                      key="tip"
                      className="am-auth-tip"
                      role="alert"
                      initial={{ opacity: 0, y: reduce ? 0 : -6, scale: reduce ? 1 : 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.22, ease: EASE }}
                    >
                      <p className="am-auth-tip-title">
                        <Icon name="alert-circle" size={18} />
                        That email and password don&apos;t match.
                      </p>
                      <p>
                        <b>Can&apos;t remember it?</b> We can email you a 6-digit code instead. Your password stays the same.
                      </p>
                      <button type="button" className="am-btn is-primary is-block" onClick={openCodeFlow}>
                        <Icon name="mail" size={18} />
                        Sign in with a code
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div key="or" className="am-auth-code-entry" exit={{ opacity: 0 }}>
                      <div className="am-auth-or"><span>or</span></div>
                      <button type="button" className="am-btn is-soft is-block" onClick={openCodeFlow}>
                        <Icon name="mail" size={18} />
                        Email me a sign-in code
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {/* Schools make the accounts, so newcomers look for the school's email
                  first; making an account yourself stays as a quiet link (N7) */}
              <div className="am-auth-newhere">
                <Icon name="mail" size={18} />
                <span>
                  <b>New here?</b> Your guidance office may have emailed you a link.{" "}
                  <button type="button" className="am-link" onClick={handleSignUp}>
                    No email? Make an account
                  </button>
                </span>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
